// §4.3 template catalog (Module 17, slice P3-B part 2).
//
// The table: code, name, category, and the per-provider × locale chip matrix
// — Telegram variants are sendable the moment they exist (`not_required`),
// WhatsApp cells wear their Meta status. Selecting a row opens the editor
// below it: the variable manager (key / sample / required — samples are
// mandatory because previews and Meta submission examples render from them,
// §2.4), and the variant editor per (provider, locale): body with
// insert-variable buttons, sample-filled live preview, buttons editor, char
// counters against the provider limits (1024 body / 60 header / 3 buttons —
// §3.5's editor blocks, mirrored client-side so the 422 is never the first
// signal).
//
// "Submit to Meta" renders PARKED-DISABLED with the D-232 reason beside it —
// a visible sentence, never a dead button doing nothing silently; the route
// itself lands at unparking (contracts: the §5 submit/sync rows are absent).
// System templates are flagged and carry no deactivate control at all — the
// server answers SYSTEM_TEMPLATE_PROTECTED, and offering the toggle would be
// a control built to be refused; the refusal path still renders verbatim
// through the failure note if another client raced us into it.
//
// ROLES (§7): edit = sales/fa/ga (`messaging.template.manage`); view =
// planner/finance (`messaging.template.read`) — for them the editor controls
// DO NOT RENDER (redaction as absence) and the readOnly line says who edits.
//
// Empty state: §4.3 declares it impossible (system templates are ensured
// per-tenant on first touch); the guard below still says something true if a
// fresh tenant's first read races the ensure.

import { useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { CheckboxField, Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import {
  DataTable,
  type DataTableColumn,
  type DataTablePager,
} from "../../../../components/data-table";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { RequestError, requestIdOf, type FieldError } from "../../../../lib/request";
import { MutationFailureNote } from "../mutation-failure-note";
import { isMessagingReadRefused, useMessagingPermissions } from "../hooks/permissions";
import {
  useCreateTemplate,
  useMessagingTemplatePages,
  usePutVariant,
  useUpdateTemplate,
  type MessageTemplate,
  type MessageTemplateVariant,
  type TemplateButton,
  type TemplateVariable,
} from "../hooks/messaging-queries";
import { TemplatePreview } from "../lib/template-preview";
import { pageRangeLabel, useLoadedPager } from "../lib/cursor-pages";
import { LOCALE_META, SUPPORTED_LOCALES, type Locale } from "@assessly/contracts/locales";
import { TEMPLATE_BUTTON_CAPS } from "@assessly/contracts";

const PROVIDERS = ["telegram", "whatsapp"] as const;
/** Every product locale a variant can be written in, from the registry
 * (D-430). */
const LOCALES = SUPPORTED_LOCALES;
type Provider = (typeof PROVIDERS)[number];
type VariantLocale = Locale;

/** §3.5 provider limits, mirrored for the counters (the server re-checks). */
const BODY_MAX = 1024;
const HEADER_MAX = 60;
const FOOTER_MAX = 60;
/** Per provider (D-593 ruling 5): Telegram holds the six-language picker,
 * WhatsApp three quick replies. One source with the server's check. */
const BUTTONS_MAX: Record<Provider, number> = TEMPLATE_BUTTON_CAPS;

const META_CHIP: Record<MessageTemplateVariant["meta_status"], ChipVariant> = {
  not_required: "ok",
  draft: "neutral",
  submitted: "accent",
  approved: "ok",
  rejected: "bad",
  paused: "warn",
  disabled: "warn",
};

/** §4.3's two reads (task #278): the active catalog, and the deactivated
 * templates the reader asks for. Module constants so the query keys are
 * stable across renders. */
const ACTIVE_QUERY = { is_active: "true" } as const;
const INACTIVE_QUERY = { is_active: "false" } as const;

export function MessagingTemplatesScreen() {
  const { t, i18n } = useTranslation();
  const perms = useMessagingPermissions();
  const canManage = perms.has("messaging.template.manage");
  const allowed = perms.canOpenTemplates;
  const refused = isMessagingReadRefused(perms, allowed);
  const readable = allowed && !refused;
  // ACTIVE FIRST (spec 17 §4.3, task #278). The catalog opens on the active
  // templates, 25 to a page by code; the deactivated ones are a second list
  // the reader opens on purpose. Until #278 the screen read the first 100
  // codes of both kinds together, and every M17-S13 run's deactivated
  // `e2e_m17_s13_…` sorts ahead of `link_welcome`.
  //
  // ONE PAGE AT A TIME (design-system §3.17 rev-7). Each list shows one page
  // and the table's pager moves between them; #278's Show more folded every
  // page read into one growing table, which past 50 rows is the list rev-7
  // forbids.
  const active = useMessagingTemplatePages(ACTIVE_QUERY, readable);
  const [showInactive, setShowInactive] = useState(false);
  const inactive = useMessagingTemplatePages(INACTIVE_QUERY, readable && showInactive);
  const activePages = useLoadedPager(active, "active");
  const inactivePages = useLoadedPager(inactive, showInactive ? "shown" : "hidden");
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  // THE OPEN TEMPLATE IS HELD, NOT ONLY FOUND (review of #278). The editor
  // used to read the selected template from the pages already loaded, so
  // deactivating it, which moves it to the deactivated list, unmounted the
  // editor whenever that list's loaded pages did not hold it (on staging every
  // M17-S13 run leaves a deactivated `e2e_m17_s13_…` that sorts first), or for
  // the moment between the two refetches, and the manager's unsaved variables
  // and chosen cell went with it. The row a click opened, refreshed from each
  // save's response, stands in whenever no loaded page holds it.
  const [pinned, setPinned] = useState<MessageTemplate | undefined>(undefined);

  if (refused) {
    return (
      <ForbiddenState title={t("messaging.access.title")} body={t("messaging.access.templates")} />
    );
  }
  if (active.isPending) {
    if (active.fetchStatus === "paused") {
      return (
        <OfflineState title={t("messaging.offline.title")} body={t("messaging.offline.body")} />
      );
    }
    return <TableSkeleton rows={6} cols={4} label={t("messaging.common.loading")} />;
  }
  if (active.isError) {
    return (
      <ErrorState
        title={t("messaging.templates.error")}
        retryLabel={t("messaging.common.retry")}
        requestId={requestIdOf(active.error)}
        requestIdLabel={t("messaging.common.requestId")}
        onRetry={() => {
          void active.refetch();
        }}
      />
    );
  }

  const rows = activePages.view.items;
  const inactiveRows = showInactive ? inactivePages.view.items : [];
  // The open template is looked up in every page read, not only the one
  // shown, so turning the page does not close the editor.
  const loaded = [
    ...active.data.pages.flatMap((page) => page.items),
    ...(showInactive ? (inactive.data?.pages.flatMap((page) => page.items) ?? []) : []),
  ];
  const selected =
    loaded.find((row) => row.id === selectedId) ??
    (pinned !== undefined && pinned.id === selectedId ? pinned : undefined);
  const pagerOf = (pages: typeof activePages): DataTablePager | undefined => {
    const view = pages.view;
    if (!view.hasPrev && !view.hasNext) return undefined;
    return {
      range: pageRangeLabel(t, i18n.language, view),
      meta: pages.turning ? t("messaging.common.loading") : undefined,
      page: view.page,
      onPage: (page) => {
        if (page < view.page) pages.prev();
        else pages.next();
      },
      hasPrev: view.hasPrev,
      hasNext: view.hasNext,
      prevLabel: t("messaging.common.pagePrev"),
      nextLabel: t("messaging.common.pageNext"),
    };
  };

  const columns: DataTableColumn<MessageTemplate>[] = [
    {
      key: "code",
      header: t("messaging.templates.colCode"),
      id: true,
      render: (row) => (
        <>
          <code>{row.code}</code>{" "}
          {row.is_system ? (
            <StatusChip variant="neutral" dot={false} data-testid="msg-tpl-system">
              {t("messaging.templates.systemChip")}
            </StatusChip>
          ) : null}
          {!row.is_active ? (
            <StatusChip variant="warn" dot={false} data-testid="msg-tpl-inactive">
              {t("messaging.templates.inactiveChip")}
            </StatusChip>
          ) : null}
        </>
      ),
    },
    { key: "name", header: t("messaging.templates.colName"), render: (row) => row.name },
    {
      key: "category",
      header: t("messaging.templates.colCategory"),
      render: (row) => t(`messaging.templates.categories.${row.category}`),
    },
    {
      key: "variants",
      header: t("messaging.templates.colVariants"),
      render: (row) => <VariantMatrix template={row} />,
    },
  ];
  const onRowClick = (row: MessageTemplate) => {
    const closing = row.id === selectedId;
    setSelectedId(closing ? undefined : row.id);
    setPinned(closing ? undefined : row);
  };

  return (
    <div className="msg-settings" data-testid="messaging-templates">
      <div className="msg-toolbar">
        {canManage ? (
          <Button
            variant="primary"
            size="sm"
            data-testid="msg-tpl-new"
            onClick={() => {
              setCreating(true);
            }}
          >
            {t("messaging.templates.new")}
          </Button>
        ) : (
          <p className="msg-hint" data-testid="msg-tpl-readonly">
            {t("messaging.templates.readOnly")}
          </p>
        )}
        <CheckboxField
          label={t("messaging.templates.showInactive")}
          checked={showInactive}
          data-testid="msg-tpl-show-inactive"
          onChange={setShowInactive}
        />
      </div>

      <section className="msg-tpl-section" data-testid="msg-tpl-section-active">
        {rows.length === 0 ? (
          <EmptyState
            title={t("messaging.templates.emptyTitle")}
            body={t("messaging.templates.emptyBody")}
          />
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            caption={t("messaging.templates.tableCaption")}
            empty={t("messaging.templates.emptyTitle")}
            rowTestId="msg-tpl-row"
            onRowClick={onRowClick}
            pager={pagerOf(activePages)}
          />
        )}
      </section>

      {showInactive ? (
        <section className="msg-tpl-section" data-testid="msg-tpl-section-inactive">
          <h3>{t("messaging.templates.inactiveTitle")}</h3>
          {inactive.isPending ? (
            <TableSkeleton rows={3} cols={4} label={t("messaging.common.loading")} />
          ) : inactive.isError ? (
            <ErrorState
              title={t("messaging.templates.error")}
              retryLabel={t("messaging.common.retry")}
              requestId={requestIdOf(inactive.error)}
              requestIdLabel={t("messaging.common.requestId")}
              onRetry={() => {
                void inactive.refetch();
              }}
            />
          ) : inactiveRows.length === 0 ? (
            <p className="msg-hint" data-testid="msg-tpl-inactive-empty">
              {t("messaging.templates.inactiveEmpty")}
            </p>
          ) : (
            <>
              <DataTable
                columns={columns}
                rows={inactiveRows}
                rowKey={(row) => row.id}
                caption={t("messaging.templates.inactiveCaption")}
                empty={t("messaging.templates.inactiveEmpty")}
                rowTestId="msg-tpl-row"
                onRowClick={onRowClick}
                pager={pagerOf(inactivePages)}
              />
            </>
          )}
        </section>
      ) : null}

      {selected !== undefined ? (
        <TemplateEditor
          key={selected.id}
          template={selected}
          canManage={canManage}
          onSaved={(updated) => {
            setPinned(updated);
            // A deactivated template moves to the deactivated list; open it,
            // so the reader sees where it went. The editor stays either way.
            if (!updated.is_active) setShowInactive(true);
          }}
          onVariantSaved={(variant) => {
            setPinned((prior) =>
              prior?.id === selected.id
                ? {
                    ...prior,
                    variants: [
                      ...prior.variants.filter(
                        (v) => v.provider !== variant.provider || v.locale !== variant.locale,
                      ),
                      variant,
                    ],
                  }
                : prior,
            );
          }}
        />
      ) : null}

      {creating ? (
        <CreateTemplateModal
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** How many rows a list holds, and the way to the rest while `next_cursor`
 * says more remain (the §4.3 locations-browser pattern). */
/** The per-provider × locale chip matrix. Only EXISTING variants render a
 * chip — a missing (provider, locale) cell is an absence, not a grey lie —
 * and each chip's accessible name composes provider + locale from data. */
function VariantMatrix({ template }: { template: MessageTemplate }) {
  const { t } = useTranslation();
  return (
    <span className="msg-matrix" data-testid="msg-tpl-matrix">
      {PROVIDERS.map((provider) =>
        LOCALES.map((locale) => {
          const variant = template.variants.find(
            (v) => v.provider === provider && v.locale === locale,
          );
          if (variant === undefined) return null;
          const label =
            provider === "telegram"
              ? t("messaging.templates.sendable")
              : t(`messaging.templates.metaStatus.${variant.meta_status}`);
          return (
            <StatusChip
              key={`${provider}-${locale}`}
              variant={META_CHIP[variant.meta_status]}
              dot={false}
              data-testid={`msg-tpl-chip-${provider}-${locale}`}
            >
              {`${t(`messaging.provider.${provider}`)} ${LOCALE_META[locale].endonym}: ${label}`}
            </StatusChip>
          );
        }),
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// the editor below the selected row
// ---------------------------------------------------------------------------

function TemplateEditor({
  template,
  canManage,
  onSaved,
  onVariantSaved,
}: {
  template: MessageTemplate;
  canManage: boolean;
  /** A PATCH succeeded; the server's template as it now stands. */
  onSaved: (updated: MessageTemplate) => void;
  /** A variant PUT succeeded; the variant as it now stands. */
  onVariantSaved: (variant: MessageTemplateVariant) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateTemplate();

  return (
    <Card title={template.name} className="msg-variant-editor" data-testid="msg-tpl-editor">
      {template.is_system ? (
        <p className="msg-hint" data-testid="msg-tpl-system-hint">
          {t("messaging.templates.systemHint")}
        </p>
      ) : canManage ? (
        <CheckboxField
          label={t("messaging.templates.activeLabel")}
          checked={template.is_active}
          disabled={update.isPending}
          data-testid="msg-tpl-active"
          onChange={(checked) => {
            update.mutate(
              { id: template.id, body: { is_active: checked } },
              { onSuccess: onSaved },
            );
          }}
        />
      ) : null}

      {canManage ? <VariableManager template={template} update={update} onSaved={onSaved} /> : null}
      {update.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.templates.updateFailed")}
          error={update.error}
        />
      ) : null}

      <VariantEditor template={template} canManage={canManage} onSaved={onVariantSaved} />
    </Card>
  );
}

function VariableManager({
  template,
  update,
  onSaved,
}: {
  template: MessageTemplate;
  update: ReturnType<typeof useUpdateTemplate>;
  onSaved: (updated: MessageTemplate) => void;
}) {
  const { t } = useTranslation();
  const [vars, setVars] = useState<readonly TemplateVariable[]>(template.variables);
  const dirty = useMemo(
    () => JSON.stringify(vars) !== JSON.stringify(template.variables),
    [vars, template.variables],
  );
  // A PATCH refused because the new variables would leave a url button
  // unrenderable (task #278) names each button and its variant. The lines
  // stand while the manager still holds the variables that were refused.
  const refusedHere =
    update.isError &&
    update.variables.id === template.id &&
    JSON.stringify(update.variables.body.variables ?? null) === JSON.stringify(vars);
  const varErrors = refusedHere ? variablesFieldErrors(update.error) : [];

  const setAt = (index: number, patch: Partial<TemplateVariable>) => {
    setVars((prior) => prior.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  };

  return (
    <div className="msg-variant-editor" data-testid="msg-tpl-vars">
      <h3>{t("messaging.templates.variablesTitle")}</h3>
      <p className="msg-hint">{t("messaging.templates.variablesHint")}</p>
      {vars.map((variable, index) => (
        <div className="msg-var-row" key={index}>
          <Field label={t("messaging.templates.varKey")}>
            <input
              value={variable.key}
              data-testid={`msg-var-key-${String(index)}`}
              onChange={(e) => {
                setAt(index, { key: e.target.value });
              }}
            />
          </Field>
          <Field label={t("messaging.templates.varSample")}>
            <input
              value={variable.sample}
              data-testid={`msg-var-sample-${String(index)}`}
              onChange={(e) => {
                setAt(index, { sample: e.target.value });
              }}
            />
          </Field>
          <CheckboxField
            label={t("messaging.templates.varRequired")}
            checked={variable.required}
            onChange={(checked) => {
              setAt(index, { required: checked });
            }}
          />
          <Button
            size="sm"
            aria-label={t("messaging.templates.removeVariable")}
            data-testid={`msg-var-remove-${String(index)}`}
            onClick={() => {
              setVars((prior) => prior.filter((_, i) => i !== index));
            }}
          >
            ✕
          </Button>
        </div>
      ))}
      <div className="msg-toolbar">
        <Button
          size="sm"
          data-testid="msg-var-add"
          onClick={() => {
            setVars((prior) => [...prior, { key: "", sample: "", required: false }]);
          }}
        >
          {t("messaging.templates.addVariable")}
        </Button>
        <Button
          size="sm"
          variant="primary"
          data-testid="msg-var-save"
          disabled={!dirty || update.isPending}
          busy={update.isPending}
          busyLabel={t("messaging.common.saving")}
          onClick={() => {
            update.mutate(
              { id: template.id, body: { variables: [...vars] } },
              { onSuccess: onSaved },
            );
          }}
        >
          {t("messaging.common.save")}
        </Button>
      </div>
      {varErrors.length > 0 ? (
        <ul className="msg-field-errors" role="alert" data-testid="msg-var-errors">
          {varErrors.map((fe, n) => {
            const provider = fe.params?.["provider"];
            const locale = fe.params?.["locale"];
            return (
              <li key={`${fe.code}-${String(n)}`} data-testid="msg-var-error">
                {typeof provider === "string" && typeof locale === "string" ? (
                  <strong>{`${t(`messaging.provider.${provider}`)} ${locale}`} </strong>
                ) : null}
                {buttonUrlFaultText(t, fe)}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// the (provider, locale) variant editor
// ---------------------------------------------------------------------------

interface VariantDraft {
  body: string;
  header: string;
  footer: string;
  buttons: readonly TemplateButton[];
}

/** The refusals the server pinned to the BODY field. Task #238: a Telegram
 * body outside spec 17 §2.5's HTML subset answers 422 with one entry per
 * fault, each carrying the offending `token` and its UTF-16 `offset`. Any
 * other failure has no body entries and keeps the failure note alone. */
export function bodyFieldErrors(error: unknown): readonly FieldError[] {
  if (!(error instanceof RequestError)) return [];
  return error.fieldErrors.filter((fe) => fe.path === "body");
}

/** Where a body fault sits: the offset the server named, and how many
 * characters to select. A token cut with "…" selects only what it quotes. */
export function faultRange(fe: FieldError): { offset: number; length: number } | undefined {
  const offset = fe.params?.["offset"];
  const token = fe.params?.["token"];
  if (typeof offset !== "number" || !Number.isInteger(offset) || offset < 0) return undefined;
  if (typeof token !== "string" || token === "") return undefined;
  return { offset, length: token.endsWith("…") ? token.length - 1 : token.length };
}

/** How many characters a reader counts in `text`: grapheme clusters, so an
 * emoji (two UTF-16 units, or several code points joined) counts once. */
function readerLength(text: string): number {
  if (typeof Intl.Segmenter !== "function") return Array.from(text).length;
  let count = 0;
  for (const _grapheme of new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(
    text,
  )) {
    count += 1;
  }
  return count;
}

/** One body refusal in the reader's language: the server's `message_key`,
 * with the offending token and a 1-based character position. The server's
 * offset is a UTF-16 index; given the body it was taken in, the position is
 * counted the way a reader counts, so each emoji before the fault is one
 * character. A key this build does not carry falls back to a sentence that
 * still names the place. */
export function bodyFaultText(t: TFunction, fe: FieldError, body?: string): string {
  const offset = fe.params?.["offset"];
  const token = fe.params?.["token"];
  const position =
    typeof offset !== "number"
      ? "?"
      : String(
          (body !== undefined && Number.isInteger(offset) && offset >= 0 && offset <= body.length
            ? readerLength(body.slice(0, offset))
            : offset) + 1,
        );
  const vars = { token: typeof token === "string" ? token : "", position };
  return t(fe.message_key, {
    ...vars,
    defaultValue: t("messaging.validation.telegram_html.fallback", vars),
  });
}

/** The url-button refusals the server pinned to BUTTONS (task #278): the
 * variant PUT renders each url button with the template's samples and
 * refuses one that does not come out as an address a customer can open,
 * path `buttons.{i}.url`. Keyed by the index in the buttons that were SENT. */
export function buttonUrlFieldErrors(error: unknown): ReadonlyMap<number, readonly FieldError[]> {
  const out = new Map<number, FieldError[]>();
  if (!(error instanceof RequestError)) return out;
  for (const fe of error.fieldErrors) {
    const match = /^buttons\.(\d+)\.url$/u.exec(fe.path);
    if (match === null) continue;
    const index = Number(match[1]);
    out.set(index, [...(out.get(index) ?? []), fe]);
  }
  return out;
}

/** The refusals a template PATCH pinned to its VARIABLES (task #278): a
 * change that would leave a variant's url button unrenderable. */
export function variablesFieldErrors(error: unknown): readonly FieldError[] {
  if (!(error instanceof RequestError)) return [];
  return error.fieldErrors.filter((fe) => fe.path === "variables");
}

/** One url-button refusal in the reader's language: the server's
 * `message_key` with the button's label and what its URL rendered to. A key
 * this build does not carry still names the button. */
export function buttonUrlFaultText(t: TFunction, fe: FieldError): string {
  const label = fe.params?.["label"];
  const token = fe.params?.["token"];
  const vars = {
    label: typeof label === "string" ? label : "",
    token: typeof token === "string" ? token : "",
  };
  return t(fe.message_key, {
    ...vars,
    defaultValue: t("messaging.validation.button_url.fallback", vars),
  });
}

function draftOf(variant: MessageTemplateVariant | undefined): VariantDraft {
  return {
    body: variant?.body ?? "",
    header: variant?.header_text ?? "",
    footer: variant?.footer_text ?? "",
    buttons: variant?.buttons ?? [],
  };
}

function VariantEditor({
  template,
  canManage,
  onSaved,
}: {
  template: MessageTemplate;
  canManage: boolean;
  onSaved: (variant: MessageTemplateVariant) => void;
}) {
  const { t } = useTranslation();
  const put = usePutVariant();
  const [provider, setProvider] = useState<Provider>("telegram");
  const [locale, setLocale] = useState<VariantLocale>("ru");
  const existing = template.variants.find((v) => v.provider === provider && v.locale === locale);
  // Seeded per (template, provider, locale); the key on the inner editor
  // remounts it — and drops any typing — exactly when the cell changes.
  const cellKey = `${template.id}|${provider}|${locale}|${existing?.updated_at ?? "new"}`;

  return (
    <div className="msg-variant-editor" data-testid="msg-variant-editor">
      <h3>{t("messaging.templates.editorTitle")}</h3>
      <div className="msg-var-row">
        <Field label={t("messaging.templates.colVariants")}>
          <Select
            value={provider}
            data-testid="msg-variant-provider"
            onChange={(e) => {
              setProvider(e.target.value as Provider);
            }}
          >
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {t(`messaging.provider.${p}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("messaging.context.language")}>
          <Select
            value={locale}
            data-testid="msg-variant-locale"
            onChange={(e) => {
              setLocale(e.target.value as VariantLocale);
            }}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l} lang={l}>
                {LOCALE_META[l].endonym}
              </option>
            ))}
          </Select>
        </Field>
        {existing !== undefined && provider === "whatsapp" ? (
          <StatusChip variant={META_CHIP[existing.meta_status]} data-testid="msg-variant-status">
            {t(`messaging.templates.metaStatus.${existing.meta_status}`)}
          </StatusChip>
        ) : null}
      </div>

      {existing?.meta_rejected_reason !== null && existing?.meta_rejected_reason !== undefined ? (
        // Meta's own words, verbatim behind the label (D-022).
        <p className="msg-error-note" data-testid="msg-variant-rejected">
          {t("messaging.templates.rejectedReason", { reason: existing.meta_rejected_reason })}
        </p>
      ) : null}

      <VariantCellEditor
        key={cellKey}
        template={template}
        provider={provider}
        locale={locale}
        existing={existing}
        canManage={canManage}
        put={put}
        onSaved={onSaved}
      />
    </div>
  );
}

function VariantCellEditor({
  template,
  provider,
  locale,
  existing,
  canManage,
  put,
  onSaved,
}: {
  template: MessageTemplate;
  provider: Provider;
  locale: VariantLocale;
  existing: MessageTemplateVariant | undefined;
  canManage: boolean;
  put: ReturnType<typeof usePutVariant>;
  onSaved: (variant: MessageTemplateVariant) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<VariantDraft>(draftOf(existing));
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const bodyErrorsId = useId();
  const buttonErrorsId = useId();
  // The faults name offsets in the body that was SENT from THIS cell, and
  // they show only while the textarea still holds that body. The mutation is
  // shared by every (provider, locale) cell, so a refusal of the ru body never
  // lands under the en one. A typed edit clears it (onChange resets the
  // mutation), and so does a remount: switching locale away and back reseeds
  // the draft from the STORED body, where the offsets point at the wrong
  // characters (task #238 review).
  const sentHere =
    put.variables?.templateId === template.id &&
    put.variables.provider === provider &&
    put.variables.locale === locale &&
    put.variables.body.body === draft.body;
  const bodyErrors = put.isError && sentHere ? bodyFieldErrors(put.error) : [];
  // The url-button refusals, placed on the DRAFT button they judged. The
  // save sends `draft.buttons` filtered to the labelled ones, and the filter
  // keeps each button object, so the i-th SENT button is found in the draft
  // by identity. An edit to a button replaces its object, and its lines go
  // with it, as the body's go with the first edit to the body.
  const urlErrors = new Map<number, readonly FieldError[]>();
  const sentCell =
    put.variables?.templateId === template.id &&
    put.variables.provider === provider &&
    put.variables.locale === locale;
  if (put.isError && sentCell) {
    const sentButtons = put.variables.body.buttons;
    for (const [sentIndex, errors] of buttonUrlFieldErrors(put.error)) {
      const sentButton = sentButtons[sentIndex];
      const draftIndex = sentButton === undefined ? -1 : draft.buttons.indexOf(sentButton);
      if (draftIndex !== -1) urlErrors.set(draftIndex, errors);
    }
  }
  const overBody = draft.body.length > BODY_MAX;
  const overHeader = draft.header.length > HEADER_MAX;
  const overFooter = draft.footer.length > FOOTER_MAX;
  const saveable =
    canManage && draft.body.trim().length > 0 && !overBody && !overHeader && !overFooter;

  if (!canManage) {
    return existing !== undefined ? (
      <p className="msg-preview" data-testid="msg-variant-preview">
        <TemplatePreview
          body={existing.body}
          provider={provider}
          variables={template.variables}
          values={{}}
        />
      </p>
    ) : (
      <p className="msg-hint">{t("messaging.templates.noVariant")}</p>
    );
  }

  return (
    <div className="msg-variant-grid">
      <div className="msg-variant-editor">
        <Field label={t("messaging.templates.body")} wide>
          <textarea
            ref={bodyRef}
            value={draft.body}
            rows={5}
            data-testid="msg-variant-body"
            aria-invalid={bodyErrors.length > 0 ? true : undefined}
            aria-describedby={bodyErrors.length > 0 ? bodyErrorsId : undefined}
            onChange={(e) => {
              if (put.isError) put.reset();
              setDraft((prior) => ({ ...prior, body: e.target.value }));
            }}
          />
        </Field>
        <span className={overBody ? "msg-counter msg-counter-over" : "msg-counter"}>
          {t("messaging.templates.counter", {
            used: String(draft.body.length),
            max: String(BODY_MAX),
          })}
        </span>
        {bodyErrors.length > 0 ? (
          <ul
            className="msg-field-errors"
            id={bodyErrorsId}
            role="alert"
            data-testid="msg-variant-body-errors"
          >
            {bodyErrors.map((fe, index) => {
              const range = faultRange(fe);
              return (
                <li key={`${fe.code}-${String(index)}`} data-testid="msg-variant-body-error">
                  <span>{bodyFaultText(t, fe, draft.body)}</span>
                  {range !== undefined ? (
                    <Button
                      size="sm"
                      data-testid="msg-variant-body-error-show"
                      onClick={() => {
                        const el = bodyRef.current;
                        if (el === null) return;
                        el.focus();
                        el.setSelectionRange(range.offset, range.offset + range.length);
                      }}
                    >
                      {t("messaging.validation.telegram_html.show")}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
        <div className="msg-toolbar">
          {template.variables.map((variable) => (
            <Button
              key={variable.key}
              size="sm"
              data-testid={`msg-insert-${variable.key}`}
              onClick={() => {
                setDraft((prior) => ({ ...prior, body: `${prior.body}{{${variable.key}}}` }));
              }}
            >
              {t("messaging.templates.insertVariable", { key: variable.key })}
            </Button>
          ))}
        </div>

        {provider === "whatsapp" ? (
          <>
            <Field label={t("messaging.templates.headerText")}>
              <input
                value={draft.header}
                data-testid="msg-variant-header"
                onChange={(e) => {
                  setDraft((prior) => ({ ...prior, header: e.target.value }));
                }}
              />
            </Field>
            <span className={overHeader ? "msg-counter msg-counter-over" : "msg-counter"}>
              {t("messaging.templates.counter", {
                used: String(draft.header.length),
                max: String(HEADER_MAX),
              })}
            </span>
            <Field label={t("messaging.templates.footerText")}>
              <input
                value={draft.footer}
                data-testid="msg-variant-footer"
                onChange={(e) => {
                  setDraft((prior) => ({ ...prior, footer: e.target.value }));
                }}
              />
            </Field>
            <span className={overFooter ? "msg-counter msg-counter-over" : "msg-counter"}>
              {t("messaging.templates.counter", {
                used: String(draft.footer.length),
                max: String(FOOTER_MAX),
              })}
            </span>
          </>
        ) : null}

        <h3>{t("messaging.templates.buttonsTitle")}</h3>
        {draft.buttons.map((button, index) => (
          <div className="msg-button-row" key={index}>
            <Field label={t("messaging.templates.buttonKind")}>
              <Select
                value={button.kind}
                data-testid={`msg-btn-kind-${String(index)}`}
                onChange={(e) => {
                  const kind = e.target.value as TemplateButton["kind"];
                  setDraft((prior) => ({
                    ...prior,
                    buttons: prior.buttons.map((b, i) => (i === index ? { ...b, kind } : b)),
                  }));
                }}
              >
                <option value="quick_reply">{t("messaging.templates.buttonQuickReply")}</option>
                <option value="url">{t("messaging.templates.buttonUrl")}</option>
              </Select>
            </Field>
            <Field label={t("messaging.templates.buttonLabel")}>
              <input
                value={button.label}
                data-testid={`msg-btn-label-${String(index)}`}
                onChange={(e) => {
                  const label = e.target.value;
                  setDraft((prior) => ({
                    ...prior,
                    buttons: prior.buttons.map((b, i) => (i === index ? { ...b, label } : b)),
                  }));
                }}
              />
            </Field>
            {button.kind === "url" ? (
              <div className="msg-button-url">
                <Field label={t("messaging.templates.buttonUrlField")}>
                  {/* A textarea that wraps, so a long link template reads
                      whole at phone width instead of scrolling inside a
                      one-line box (task #278). A link has no line breaks,
                      so typed or pasted ones are dropped. */}
                  <textarea
                    rows={2}
                    className="msg-url-field"
                    value={button.url ?? ""}
                    data-testid={`msg-btn-url-${String(index)}`}
                    aria-invalid={urlErrors.has(index) ? true : undefined}
                    aria-describedby={
                      urlErrors.has(index) ? `${buttonErrorsId}-${String(index)}` : undefined
                    }
                    onChange={(e) => {
                      if (put.isError) put.reset();
                      const url = e.target.value.replace(/[\r\n]+/gu, "");
                      setDraft((prior) => ({
                        ...prior,
                        buttons: prior.buttons.map((b, i) => (i === index ? { ...b, url } : b)),
                      }));
                    }}
                  />
                </Field>
                {urlErrors.has(index) ? (
                  <ul
                    className="msg-field-errors"
                    id={`${buttonErrorsId}-${String(index)}`}
                    role="alert"
                    data-testid={`msg-btn-url-errors-${String(index)}`}
                  >
                    {(urlErrors.get(index) ?? []).map((fe, n) => (
                      <li key={`${fe.code}-${String(n)}`} data-testid="msg-btn-url-error">
                        {buttonUrlFaultText(t, fe)}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <Button
              size="sm"
              aria-label={t("messaging.templates.removeButton")}
              data-testid={`msg-btn-remove-${String(index)}`}
              onClick={() => {
                setDraft((prior) => ({
                  ...prior,
                  buttons: prior.buttons.filter((_, i) => i !== index),
                }));
              }}
            >
              ✕
            </Button>
          </div>
        ))}
        <div className="msg-toolbar">
          {draft.buttons.length < BUTTONS_MAX[provider] ? (
            <Button
              size="sm"
              data-testid="msg-btn-add"
              onClick={() => {
                setDraft((prior) => ({
                  ...prior,
                  buttons: [...prior.buttons, { kind: "quick_reply", label: "" }],
                }));
              }}
            >
              {t("messaging.templates.addButton")}
            </Button>
          ) : null}
          <span className="msg-counter">
            {t("messaging.templates.counter", {
              used: String(draft.buttons.length),
              max: String(BUTTONS_MAX[provider]),
            })}
          </span>
        </div>

        <div className="msg-toolbar">
          <Button
            variant="primary"
            size="sm"
            data-testid="msg-variant-save"
            disabled={!saveable || put.isPending}
            busy={put.isPending}
            busyLabel={t("messaging.common.saving")}
            onClick={() => {
              put.mutate(
                {
                  templateId: template.id,
                  provider,
                  locale,
                  body: {
                    body: draft.body,
                    ...(provider === "whatsapp" && draft.header.trim().length > 0
                      ? { header_text: draft.header }
                      : {}),
                    ...(provider === "whatsapp" && draft.footer.trim().length > 0
                      ? { footer_text: draft.footer }
                      : {}),
                    buttons: draft.buttons.filter((b) => b.label.trim().length > 0),
                  },
                },
                { onSuccess: onSaved },
              );
            }}
          >
            {t("messaging.templates.saveVariant")}
          </Button>
          {provider === "whatsapp" ? (
            <>
              {/* PARKED-DISABLED with its reason (D-232): the submit route
               * lands at unparking; a button that silently no-ops is the
               * defect this sentence exists to prevent. */}
              <Button size="sm" disabled data-testid="msg-submit-meta">
                {t("messaging.templates.submitMeta")}
              </Button>
              <p className="msg-hint" data-testid="msg-submit-parked">
                {t("messaging.templates.submitParked")}
              </p>
            </>
          ) : null}
        </div>
        {put.isError ? (
          <MutationFailureNote
            data-testid="msg-mutation-failed"
            text={t("messaging.templates.variantFailed")}
            error={put.error}
          />
        ) : null}
      </div>

      <div className="msg-variant-editor">
        <h3>{t("messaging.templates.preview")}</h3>
        {draft.body.trim().length > 0 ? (
          <p className="msg-preview" data-testid="msg-variant-preview">
            <TemplatePreview
              body={draft.body}
              provider={provider}
              variables={template.variables}
              values={{}}
            />
          </p>
        ) : (
          <p className="msg-hint">{t("messaging.templates.previewEmpty")}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// create
// ---------------------------------------------------------------------------

function CreateTemplateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateTemplate();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState<MessageTemplate["category"]>("utility");
  const valid = /^[a-z][a-z0-9_]{1,63}$/.test(code) && name.trim().length > 0;

  return (
    <Modal
      label={t("messaging.templates.createTitle")}
      heading={t("messaging.templates.createTitle")}
      closeLabel={t("messaging.common.close")}
      className="msg-modal"
      testId="msg-tpl-create"
      onClose={onClose}
    >
      <div className="msg-variant-editor">
        <Field label={t("messaging.templates.code")}>
          <input
            value={code}
            placeholder={t("messaging.templates.codeHint")}
            data-testid="msg-tpl-code"
            onChange={(e) => {
              setCode(e.target.value);
            }}
          />
        </Field>
        <Field label={t("messaging.templates.name")}>
          <input
            value={name}
            data-testid="msg-tpl-name"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </Field>
        <Field label={t("messaging.templates.category")}>
          <Select
            value={category}
            data-testid="msg-tpl-category"
            onChange={(e) => {
              setCategory(e.target.value as MessageTemplate["category"]);
            }}
          >
            <option value="utility">{t("messaging.templates.categories.utility")}</option>
            <option value="marketing">{t("messaging.templates.categories.marketing")}</option>
            <option value="authentication">
              {t("messaging.templates.categories.authentication")}
            </option>
          </Select>
        </Field>
        <div className="msg-toolbar">
          <Button size="sm" onClick={onClose}>
            {t("messaging.common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="msg-tpl-create-submit"
            disabled={!valid || create.isPending}
            busy={create.isPending}
            busyLabel={t("messaging.common.saving")}
            onClick={() => {
              create.mutate(
                { code, name: name.trim(), category, variables: [] },
                { onSuccess: onClose },
              );
            }}
          >
            {t("messaging.common.save")}
          </Button>
        </div>
        {create.isError ? (
          <MutationFailureNote
            data-testid="msg-mutation-failed"
            text={t("messaging.templates.createFailed")}
            error={create.error}
          />
        ) : null}
      </div>
    </Modal>
  );
}
